import React, { useEffect } from "react"
import { Navigate, useRouteError } from "react-router-dom"
import { isMobile } from "./utils/detectDevice"
import * as Sentry from "@sentry/react"
import { useTranslation } from "react-i18next"

const getRedirectLocationFromError = (error: unknown): string | null => {
  if (!(error instanceof Response)) return null
  if (error.status < 300 || error.status >= 400) return null
  return error.headers.get("Location")
}

export const RouterErrorHandler = ({ path }: { path?: string }) => {
  const { t } = useTranslation()
  const routeError = useRouteError()
  const redirectTo = getRedirectLocationFromError(routeError)

  useEffect(() => {
    if (redirectTo) return
    window.analytics.track("AppCrash", { error: routeError })
    captureError(routeError, { origin: "ErrorBoundary" })
  }, [routeError, redirectTo])

  if (redirectTo) {
    return <Navigate to={redirectTo} replace />
  }

  const onClickReload = () => {
    window.location.href = path ?? "/"
  }

  const submessage = isMobile
    ? t("app.errorPage.textMobile")
    : t("app.errorPage.textDesktop")

  return (
    <>
      <main className="grid place-items-center bg-slate-900 p-4">
        <img src="slickshift2.png" className="w-52" />
      </main>
      <main className="grid min-h-full place-items-center px-6 py-24 sm:py-4">
        <div className="text-center">
          <h1 className="mt-4 text-xl font-bold tracking-tight text-gray-900 sm:text-xl">
            {t("app.errorPage.title")}
          </h1>
          <p className="mt-6 text-base leading-7 text-gray-600">{submessage}</p>
          <div className="mt-10 flex items-center justify-center gap-x-6">
            {!isMobile && (
              <a
                onClick={onClickReload}
                className="rounded-md bg-indigo-600 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
              >
                {t("app.errorPage.refresh")}
              </a>
            )}
          </div>
        </div>
      </main>
    </>
  )
}

type CaptureOptions = {
  origin:
    | "XMPPErrorCallback"
    | "XMPPErrorStanza"
    | "XMPPOnOnline"
    | "ErrorBoundary"
    | "UserAction"
    | "InitialLoad"
    | "PushNotifications"
    | "Map"
    | "Other"
  extra?: Record<string, string | number>
}

export const captureError = (error: any, options: CaptureOptions) => {
  console.debug(error, options)
  Sentry.captureException(error, {
    tags: { origin: options.origin },
    extra: options.extra,
  })
}
